import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FireForesightService } from 'src/api/services';
import { Camera, Incident, StatusType } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { WatchViewItem } from '../../views/applications/fire-foresight/watch/fire-foresight-watch.component';

interface SelectableCamera {
  camera: Camera;
  selected: boolean;
}

interface SelectableIncident {
  incident: Incident;
  selected: boolean;
}

interface SelectableStatus {
  statusType: StatusType;
  selected: boolean;
  incidentCount: number;
}

@Component({
  selector: 'app-camera-selection-modal',
  templateUrl: './camera-selection-modal.component.html',
  styleUrls: ['./camera-selection-modal.component.scss']
})
export class CameraSelectionModalComponent implements OnInit {
  @Input() existingItems: WatchViewItem[] = [];
  @Input() maxItems: number = 9;
  @Output() close = new EventEmitter<void>();
  @Output() itemsSelected = new EventEmitter<WatchViewItem[]>();

  public activeTab: 'cameras' | 'incidents' | 'statuses' = 'cameras';
  public cameras: SelectableCamera[] = [];
  public incidents: SelectableIncident[] = [];
  public statuses: SelectableStatus[] = [];
  public loadingCameras = true;
  public loadingIncidents = true;
  public loadingStatuses = true;
  public errorCameras = false;
  public errorIncidents = false;
  public errorStatuses = false;
  public searchTerm = '';

  constructor(
    public app: ApplicationConfigService,
    private fireForesightService: FireForesightService
  ) {}

  ngOnInit(): void {
    this.loadCameras();
    this.loadIncidents();
    this.loadStatuses();
  }

  private loadCameras(): void {
    this.loadingCameras = true;
    this.errorCameras = false;

    this.fireForesightService.FireForesightGetCameras(
      this.app.customerId
    ).subscribe({
      next: (cameras: Camera[]) => {
        this.cameras = cameras.map(camera => ({
          camera,
          selected: false
        }));
        this.loadingCameras = false;
      },
      error: (err) => {
        console.error('Error loading cameras:', err);
        this.errorCameras = true;
        this.loadingCameras = false;
      }
    });
  }

  private loadIncidents(): void {
    this.loadingIncidents = true;
    this.errorIncidents = false;

    // Load active incidents (siteUri null to get all sites)
    this.fireForesightService.FireForesightGetIncidents({
      customerId: this.app.customerId,
      statusId: null, // Active incidents only (adjust based on your status IDs)
      siteUri: null // Get incidents from all sites
    }).subscribe({
      next: (incidents: Incident[]) => {
        this.incidents = incidents.map(incident => ({
          incident,
          selected: false
        }));
        this.loadingIncidents = false;
      },
      error: (err) => {
        console.error('Error loading incidents:', err);
        this.errorIncidents = true;
        this.loadingIncidents = false;
      }
    });
  }

  private loadStatuses(): void {
    this.loadingStatuses = true;
    this.errorStatuses = false;

    // Load status types and filter by visible flag
    this.fireForesightService.FireForesightGetStatusTypes().subscribe({
      next: (statusTypes: StatusType[]) => {
        // Only show statuses that are marked as visible
        this.statuses = statusTypes
          .filter(st => st.visible)
          .map(st => ({
            statusType: st,
            selected: false,
            incidentCount: 0 // Not used anymore
          }));
        this.loadingStatuses = false;
      },
      error: (err) => {
        console.error('Error loading status types:', err);
        this.errorStatuses = true;
        this.loadingStatuses = false;
      }
    });
  }

  public setTab(tab: 'cameras' | 'incidents' | 'statuses'): void {
    this.activeTab = tab;
    this.searchTerm = '';
  }

  public toggleCamera(selectableCamera: SelectableCamera): void {
    if (this.canSelectMore() || selectableCamera.selected) {
      selectableCamera.selected = !selectableCamera.selected;
    }
  }

  public toggleIncident(selectableIncident: SelectableIncident): void {
    if (this.canSelectMore() || selectableIncident.selected) {
      selectableIncident.selected = !selectableIncident.selected;
    }
  }

  public toggleStatus(selectableStatus: SelectableStatus): void {
    if (this.canSelectMore() || selectableStatus.selected) {
      selectableStatus.selected = !selectableStatus.selected;
    }
  }

  public onConfirm(): void {
    const selectedItems: WatchViewItem[] = [];

    // Add selected cameras
    this.cameras
      .filter(c => c.selected)
      .forEach(c => {
        selectedItems.push({
          type: 'camera',
          id: c.camera.siteId ? c.camera.siteId.toString() : '',
          name: c.camera.name || 'Unknown Camera',
          uri: c.camera.siteUri
        });
      });

    // Add selected incidents
    this.incidents
      .filter(i => i.selected)
      .forEach(i => {
        selectedItems.push({
          type: 'incident',
          id: i.incident.incidentId ? i.incident.incidentId.toString() : '',
          name: i.incident.site?.name || 'Unknown Incident'
        });
      });

    // Add selected statuses
    this.statuses
      .filter(s => s.selected)
      .forEach(s => {
        selectedItems.push({
          type: 'incident-status',
          id: s.statusType.statusId.toString(),
          name: s.statusType.displayName,
          statusId: s.statusType.statusId,
          statusName: s.statusType.displayName
        });
      });

    this.itemsSelected.emit(selectedItems);
  }

  public onClose(): void {
    this.close.emit();
  }

  public get selectedCount(): number {
    return this.cameras.filter(c => c.selected).length + 
           this.incidents.filter(i => i.selected).length +
           this.statuses.filter(s => s.selected).length;
  }

  public get availableSlots(): number {
    return this.maxItems - this.existingItems.length;
  }

  public canSelectMore(): boolean {
    return this.selectedCount < this.availableSlots;
  }

  public get filteredCameras(): SelectableCamera[] {
    if (!this.searchTerm) {
      return this.cameras;
    }
    const term = this.searchTerm.toLowerCase();
    return this.cameras.filter(c => 
      c.camera.name?.toLowerCase().includes(term) ||
      c.camera.siteUri?.toLowerCase().includes(term)
    );
  }

  public get filteredIncidents(): SelectableIncident[] {
    if (!this.searchTerm) {
      return this.incidents;
    }
    const term = this.searchTerm.toLowerCase();
    return this.incidents.filter(i => 
      i.incident.site?.name?.toLowerCase().includes(term) ||
      i.incident.incidentId?.toString().includes(term)
    );
  }

  public isCameraAlreadyAdded(camera: Camera): boolean {
    return this.existingItems.some(item => 
      item.type === 'camera' && item.id === camera.siteId?.toString()
    );
  }

  public isIncidentAlreadyAdded(incident: Incident): boolean {
    return this.existingItems.some(item => 
      item.type === 'incident' && item.id === incident.incidentId?.toString()
    );
  }
}
